import React, { ReactNode, useState } from 'react'
import { Close, Warning } from '@mui/icons-material'
import cn from '../../lib/utils/cn'
import IconButton from './IconButton'
import { ClassNameValue } from 'tailwind-merge'
import ConfirmationModal from '../modals/common/ConfirmationModal'

export interface DialogProps {
	isOpen: boolean
	onClose: () => void
	children: React.ReactNode
	title?: string | ReactNode
	icon?: ReactNode
	controllers?: ReactNode
	className?: ClassNameValue
	overlayClassName?: ClassNameValue
	contentClassName?: ClassNameValue
	titleClassName?: ClassNameValue
	controllersWrapperClassName?: ClassNameValue
	hideCloseButton?: boolean
	closeByClickOverlay?: boolean
	withMandatoryTooltip?: boolean
	isDirty?: boolean
}

const Dialog: React.FC<DialogProps> = ({
	isOpen,
	onClose,
	children,
	title,
	icon,
	controllers,
	contentClassName,
	titleClassName,
	controllersWrapperClassName,
	overlayClassName,
	className,
	hideCloseButton,
	closeByClickOverlay = true,
	withMandatoryTooltip = false,
	isDirty = false,
}) => {
	const [isWarningModalOpen, setIsWarningModalOpen] = useState(false)

	const handleClose = () => {
		if (isDirty) {
			setIsWarningModalOpen(true)
			return
		}
		onClose()
	}

	if (!isOpen) return null

	return (
		<>
			<ConfirmationModal
				isOpen={isWarningModalOpen}
				onClose={() => setIsWarningModalOpen(false)}
				onAction={() => {
					setIsWarningModalOpen(false)
					onClose()
				}}
				onCancel={() => setIsWarningModalOpen(false)}
				icon={<Warning className="fill-accent-disabled" />}
				title="You have unsaved changes"
				description="Changes will not be saved after closing. What do you want to do?"
				actionLabel="Exit without saving"
				cancelLabel="Return to edit"
				overlayClassName="bg-black bg-opacity-[28%] z-[102]"
				closeByClickOverlay={false}
			/>
			<div
				className={cn(
					'fixed inset-0 bg-black flex items-center justify-center z-[101]',
					isWarningModalOpen ? 'bg-opacity-30' : 'bg-opacity-50',
					overlayClassName
				)}
				onClick={() => {
					closeByClickOverlay && handleClose()
				}}
			>
				<div
					className={cn(
						`bg-white dark:bg-background-dark rounded-lg relative min-w-[340px] max-sm:min-w-[150px] max-sm:w-4/5 max-h-[90%] max-w-[500px] overflow-y-auto`,
						// `bg-white dark:bg-background-dark rounded-lg relative min-w-[340px] max-sm:min-w-[150px] max-sm:max-w-full max-sm:w-full xs:w-4/5 xs:max-w-4/5 max-h-[90%] md:max-w-[500px] overflow-y-auto`,
						hideCloseButton ? 'p-3' : 'sm:p-6 p-3',
						className
					)}
					onClick={(e) => e.stopPropagation()}
				>
					{!hideCloseButton && (
						<IconButton
							variant="transparent"
							className="absolute top-2 right-2"
							onClick={handleClose}
							icon={
								<Close className="w-5 h-5 fill-text-secondary dark:fill-text-main-dark" />
							}
						/>
					)}

					<h3
						className={cn(
							'flex items-center gap-2 pr-10 text-text-main dark:text-text-main-dark font-semibold text-lg pb-2'
						)}
					>
						{icon && icon}
						<div
							className={cn(
								'ellipsis font-bold text-xl md:text-2xl text-text-main dark:text-text-main-dark',
								titleClassName
							)}
						>
							{title}
						</div>
					</h3>

					<div
						className={cn(
							`${hideCloseButton ? 'mt-2' : 'mt-7'} w-full`,
							contentClassName
						)}
					>
						{children}
					</div>
					{!!controllers && (
						<div
							className={cn(
								'mt-5 w-full',
								withMandatoryTooltip &&
								'flex max-[350px]:flex-col flex-row  max-[350px]:items-end items-center justify-between',
								controllersWrapperClassName
							)}
						>
							{withMandatoryTooltip && (
								<div className="flex items-center justify-start text-xs text-text-secondary dark:text-stroke-dark max-[350px]:mb-1 gap-1 max-[350px]:mr-auto">
									<p className="text-lg h-6 text-accent">*</p>
									<p className=""> - required </p>
								</div>
							)}
							{controllers}
						</div>
					)}
				</div>
			</div>
		</>
	)
}

export default Dialog
